import { useState, useEffect } from 'react';
import axios from 'axios';

// 表情数组
const emojis = [
    '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
    '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
    '😘', '😗', '😚', '😋', '😛', '😜', '🤪', '😝',
    '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐',
    '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌',
    '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢',
    '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠',
    '🥳', '😎', '🤓', '🧐', '👍', '👎', '👏', '🙌',
    '🤝', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤',
    '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘'
];

function Stats() {
  const [password, setPassword] = useState('');
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  
  // 设置结束日期为当天（初始化时）
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  
  const [endDate, setEndDate] = useState(getTodayDate());
  const [showIpModal, setShowIpModal] = useState(false);
  const [ipDetails, setIpDetails] = useState(null);
  const [ipLoading, setIpLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('ip-list');
  const [selectedVersion, setSelectedVersion] = useState('all');
  
  // 留言管理相关状态
  const [activeMainTab, setActiveMainTab] = useState('traffic'); // 'traffic' 或 'guestbook' 或 'password'
  const [guestbookData, setGuestbookData] = useState(null);
  const [guestbookLoading, setGuestbookLoading] = useState(false);
  const [guestbookFilter, setGuestbookFilter] = useState('all'); // 'all', 0, 1, 2
  const [replyingMessageId, setReplyingMessageId] = useState(null); // 正在回复的留言ID
  const [replyContent, setReplyContent] = useState(''); // 回复内容
  const [showEmojiPicker, setShowEmojiPicker] = useState(false); // 表情选择器显示状态

  // 密码找回相关状态
  const [recoverUsername, setRecoverUsername] = useState('');
  const [recoverLoading, setRecoverLoading] = useState(false);
  const [recoverResult, setRecoverResult] = useState(null);

  // 记录页面访问
  useEffect(() => {
    const recordPageView = async () => {
      try {
        // 获取当前页面路径
        const pagePath = window.location.pathname;
        
        // 尝试获取客户端IP
        let clientIp = '';
        try {
          // 方法1: 使用第三方服务获取IP
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipResponse.json();
          clientIp = ipData.ip;
        } catch (ipError) {
          console.log('获取客户端IP失败:', ipError.message);
        }
        
        // 发送请求记录页面访问
        await axios.post('/api/pageview', {
          page: pagePath,
          referer: document.referrer || '',
          userAgent: navigator.userAgent,
          clientIp: clientIp || 'unknown',
          // 检测用户是否来自经典版
          isClassicUser: document.referrer.includes('clas.wqqp.cn') || window.location.href.includes('/clas/')
        }, {
          timeout: 5000  // 5秒超时
        });
      } catch (error) {
        // 静默失败，不干扰用户
        console.log('页面访问记录失败（不影响使用）:', error.message);
      }
    };

    // 延迟执行，避免影响页面加载
    setTimeout(recordPageView, 1000);
  }, []);

  // 点击外部关闭表情选择器
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && !event.target.closest('.emoji-picker-container')) {
        setShowEmojiPicker(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const fetchStats = async () => {
    if (!password) {
      setError('请输入密码');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const params = { password };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await axios.get('/api/stats/traffic', { params });
      setStats(response.data);
      setIpDetails(null); // 重置IP详情数据
      
      // 自动加载留言数据
      fetchGuestbook();
    } catch (err) {
      setError(err.response?.data?.error || '获取统计失败');
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchIpDetails = async (version = 'all') => {
    if (!password) return;

    setIpLoading(true);
    try {
      const params = { password };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (version !== 'all') params.version = version;
      
      const response = await axios.get('/api/stats/ips', { params });
      setIpDetails(response.data);
    } catch (err) {
      console.error('获取IP详情失败:', err);
      alert(err.response?.data?.error || '获取IP详情失败');
    } finally {
      setIpLoading(false);
    }
  };

  const handleShowIpModal = async (version = 'all') => {
    setSelectedVersion(version);
    setShowIpModal(true);
    setActiveTab('ip-list');
    if (!ipDetails || version !== 'all') {
      await fetchIpDetails(version);
    }
  };
  
  // 获取留言列表
  const fetchGuestbook = async () => {
    if (!password) return;
    
    setGuestbookLoading(true);
    try {
      const params = { password, limit: 100, offset: 0 };
      const response = await axios.get('/api/admin/guestbook/list', { params });
      setGuestbookData(response.data);
    } catch (err) {
      console.error('获取留言列表失败:', err);
      alert(err.response?.data?.error || '获取留言列表失败');
    } finally {
      setGuestbookLoading(false);
    }
  };
  
  // 更新留言状态
  const updateMessageStatus = async (messageId, newStatus) => {
    if (!confirm(`确定要${newStatus === 1 ? '显示' : newStatus === 2 ? '隐藏' : '设为待审核'}这条留言吗？`)) {
      return;
    }
    
    try {
      await axios.put(`/api/admin/guestbook/${messageId}/status`, {
        password,
        status: newStatus
      });
      alert('状态更新成功');
      fetchGuestbook(); // 刷新列表
    } catch (err) {
      console.error('更新状态失败:', err);
      alert(err.response?.data?.error || '更新状态失败');
    }
  };
  
  // 删除留言
  const deleteMessage = async (messageId) => {
    if (!confirm('确定要删除这条留言吗？此操作不可恢复！')) {
      return;
    }
    
    try {
      await axios.delete(`/api/admin/guestbook/${messageId}`, {
        data: { password }
      });
      alert('删除成功');
      fetchGuestbook(); // 刷新列表
    } catch (err) {
      console.error('删除失败:', err);
      alert(err.response?.data?.error || '删除失败');
    }
  };
  
  // 开始回复
  const startReply = (messageId, currentReply = '') => {
    setReplyingMessageId(messageId);
    setReplyContent(currentReply || '');
  };
  
  // 取消回复
  const cancelReply = () => {
    setReplyingMessageId(null);
    setReplyContent('');
  };
  
  // 提交回复
  const submitReply = async (messageId) => {
    if (!replyContent || !replyContent.trim()) {
      alert('回复内容不能为空');
      return;
    }
    
    try {
      await axios.put(`/api/admin/guestbook/${messageId}/reply`, {
        password,
        reply: replyContent.trim()
      });
      alert('回复成功');
      setReplyingMessageId(null);
      setReplyContent('');
      fetchGuestbook(); // 刷新列表
    } catch (err) {
      console.error('回复失败:', err);
      alert(err.response?.data?.error || '回复失败');
    }
  };
  
  // 删除回复
  const deleteReply = async (messageId) => {
    if (!confirm('确定要删除这条回复吗？')) {
      return;
    }
    
    try {
      await axios.delete(`/api/admin/guestbook/${messageId}/reply`, {
        data: { password }
      });
      alert('删除回复成功');
      fetchGuestbook(); // 刷新列表
    } catch (err) {
      console.error('删除回复失败:', err);
      alert(err.response?.data?.error || '删除回复失败');
    }
  };
  
  // 插入表情
  const insertEmoji = (emoji) => {
    setReplyContent(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // 密码找回
  const handleRecoverPassword = async () => {
    if (!recoverUsername.trim()) {
      setRecoverResult({ error: '请输入用户名' });
      return;
    }
    setRecoverLoading(true);
    setRecoverResult(null);
    try {
      const response = await axios.post('/api/admin/recover-password', {
        password,
        username: recoverUsername.trim()
      });
      setRecoverResult(response.data);
    } catch (err) {
      setRecoverResult({ error: err.response?.data?.error || '找回密码失败' });
    } finally {
      setRecoverLoading(false);
    }
  };

  const getVersionInfo = (version) => {
    const map = {
      'modern': { name: '新版', color: 'blue', borderClass: 'border-l-blue-500' },
      'classic': { name: '经典版', color: 'green', borderClass: 'border-l-green-500' },
      'unknown': { name: '未知', color: 'gray', borderClass: 'border-l-gray-500' }
    };
    return map[version] || map['unknown'];
  };

  const getVersionColor = (version) => {
    const colors = {
      'modern': 'bg-blue-500',
      'classic': 'bg-green-500',
      'unknown': 'bg-gray-500'
    };
    return colors[version] || colors['unknown'];
  };

  const getVersionBadgeClass = (version) => {
    const classes = {
      'modern': 'bg-blue-100 text-blue-800',
      'classic': 'bg-green-100 text-green-800',
      'unknown': 'bg-gray-100 text-gray-800'
    };
    return classes[version] || classes['unknown'];
  };

  const getVersionName = (version) => {
    const names = {
      'modern': '新版',
      'classic': '经典版',
      'unknown': '未知'
    };
    return names[version] || '未知';
  };

  const getColorClass = (color) => {
    const classes = {
      'blue': 'text-blue-600',
      'green': 'text-green-600',
      'gray': 'text-gray-600'
    };
    return classes[color] || classes['gray'];
  };

  if (!stats) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <h1 className="text-2xl font-bold mb-6">📊 流量统计</h1>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">密码</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchStats()}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="请输入统计密码"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">开始日期 (可选)</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">结束日期 (可选)</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {error && <div className="text-red-600">{error}</div>}
            
            <button
              onClick={fetchStats}
              disabled={loading}
              className="w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 disabled:bg-gray-400"
            >
              {loading ? '加载中...' : '查看统计'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">📊 管理后台</h1>
      
      <div className="space-y-6">
        <button
          onClick={() => setStats(null)}
          className="text-blue-500 hover:text-blue-700"
        >
          ← 返回
        </button>
        
        {/* 主标签页切换 */}
        <div className="flex border-b border-gray-200">
          <button
            className={`px-4 py-2 border-b-2 ${
              activeMainTab === 'traffic'
                ? 'border-blue-500 text-blue-600 font-medium'
                : 'border-transparent text-gray-500'
            }`}
            onClick={() => setActiveMainTab('traffic')}
          >
            📊 流量统计
          </button>
          <button
            className={`px-4 py-2 border-b-2 ${
              activeMainTab === 'guestbook'
                ? 'border-blue-500 text-blue-600 font-medium'
                : 'border-transparent text-gray-500'
            }`}
            onClick={() => {
              setActiveMainTab('guestbook');
              if (!guestbookData) {
                fetchGuestbook();
              }
            }}
          >
            💬 留言管理
            {guestbookData?.status_counts?.[0] > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {guestbookData.status_counts[0]}
              </span>
            )}
          </button>
          <button
            className={`px-4 py-2 border-b-2 ${
              activeMainTab === 'password'
                ? 'border-blue-500 text-blue-600 font-medium'
                : 'border-transparent text-gray-500'
            }`}
            onClick={() => setActiveMainTab('password')}
          >
            🔑 密码找回
          </button>
        </div>
        
        {/* 流量统计内容 */}
        {activeMainTab === 'traffic' && (
          <>
            {/* 概览卡片 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-gray-600 text-sm">总访问量</div>
                <div className="text-3xl font-bold text-blue-600">{stats.total_views}</div>
              </div>
              <div 
                className="bg-white p-6 rounded-lg shadow cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={handleShowIpModal}
                title="点击查看IP列表"
              >
                <div className="text-gray-600 text-sm">独立IP数 👆</div>
                <div className="text-3xl font-bold text-green-600">{stats.unique_ips}</div>
              </div>
            </div>
            
            {/* 版本分布卡片 */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-bold mb-4">📱 版本分布</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(stats.version_stats || []).map((v, index) => {
                  const info = getVersionInfo(v.version);
                  return (
                    <div key={index} className={`bg-gray-50 p-4 rounded border-l-4 ${info.borderClass}`}>
                      <div className="text-sm text-gray-600">{info.name}</div>
                      <div className={`text-2xl font-bold ${getColorClass(info.color)}`}>{v.count}</div>
                      <div 
                        className="text-sm text-gray-500 cursor-pointer hover:text-blue-600 transition-colors flex items-center gap-1"
                        onClick={() => handleShowIpModal(v.version)}
                        title={`点击查看${info.name}的IP列表`}
                      >
                        <span>{v.unique_ips}</span>
                        <span>个独立IP</span>
                        <span className="text-xs opacity-70">👆</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* 每日统计 */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-bold mb-4">📅 最近30天访问趋势</h2>
              <div className="space-y-2">
                {(stats.daily_stats || []).map((day) => {
                  const versions = day.versions || {};
                  const total = Object.values(versions).reduce((a, b) => a + b, 0);
                  const dayMax = Math.max(...Object.values(versions));
                  
                  return (
                    <div key={day.date} className="flex items-center gap-4">
                      <div className="w-28 text-sm text-gray-600">{String(day.date).slice(0, 10)}</div>
                      <div className="flex-1 flex gap-1 h-4">
                        {Object.entries(versions).map(([ver, count]) => (
                          <div
                            key={ver}
                            className={`${getVersionColor(ver)} rounded`}
                            style={{
                              width: `${(count / (dayMax || 1)) * 100}%`,
                              minWidth: count > 0 ? '4px' : '0'
                            }}
                          />
                        ))}
                      </div>
                      <div className="w-12 text-sm font-medium text-right">{total}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* 热门页面 */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-bold mb-4">🔥 热门页面</h2>
              <div className="space-y-2">
                {(stats.page_stats || []).map((page, index) => {
                  const versions = page.versions || {};
                  
                  return (
                    <div key={page.page} className="flex items-center gap-4">
                      <div className="w-8 text-center font-bold text-gray-500">#{index + 1}</div>
                      <div className="flex-1">
                        <div className="text-sm">{page.page || '/'}</div>
                        <div className="flex gap-2 mt-1">
                          {Object.entries(versions).map(([ver, count]) => (
                            <span
                              key={ver}
                              className={`text-xs px-2 py-0.5 rounded ${getVersionBadgeClass(ver)}`}
                            >
                              {getVersionName(ver)}: {count}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="w-16 text-right font-medium">{page.total}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
        
        {/* 留言管理内容 */}
        {activeMainTab === 'guestbook' && (
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">💬 留言管理</h2>
              <button
                onClick={fetchGuestbook}
                disabled={guestbookLoading}
                className="text-blue-500 hover:text-blue-700 text-sm"
              >
                🔄 刷新
              </button>
            </div>
            {guestbookData && (
              <div className="flex gap-4 mb-4 text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">总数:</span>
                  <span className="font-bold">{guestbookData.total}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-yellow-600">待审核:</span>
                  <span className="font-bold">{guestbookData.status_counts?.[0] || 0}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-green-600">已显示:</span>
                  <span className="font-bold">{guestbookData.status_counts?.[1] || 0}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-red-600">已隐藏:</span>
                  <span className="font-bold">{guestbookData.status_counts?.[2] || 0}</span>
                </div>
              </div>
            )}
            
            {/* 筛选器 */}
            <div className="flex gap-2 mb-4">
              {[
                { key: 'all', label: '全部' },
                { key: 0, label: '待审核' },
                { key: 1, label: '已显示' },
                { key: 2, label: '已隐藏' }
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setGuestbookFilter(filter.key)}
                  className={`px-3 py-1 rounded text-sm ${
                    guestbookFilter === filter.key
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            
            {guestbookLoading ? (
              <div className="text-center py-8">加载中...</div>
            ) : !guestbookData ? (
              <div className="text-center py-8 text-gray-500">暂无数据</div>
            ) : (
              <div className="space-y-4">
                {(guestbookData.messages || [])
                  .filter(msg => guestbookFilter === 'all' || msg.status === guestbookFilter)
                  .map((msg) => (
                    <div 
                      key={msg.id} 
                      className={`border rounded-lg p-4 ${
                        msg.status === 0 ? 'border-yellow-300 bg-yellow-50' :
                        msg.status === 2 ? 'border-red-300 bg-red-50' :
                        'border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-bold">{msg.nickname}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            msg.status === 0 ? 'bg-yellow-200 text-yellow-800' :
                            msg.status === 2 ? 'bg-red-200 text-red-800' :
                            'bg-green-200 text-green-800'
                          }`}>
                            {msg.status === 0 ? '待审核' : msg.status === 2 ? '已隐藏' : '已显示'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {msg.created_at?.slice(0, 16).replace('T', ' ')}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 mb-2 whitespace-pre-wrap">{msg.content}</p>
                      
                      {/* 显示已有回复 */}
                      {msg.reply && (
                        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-2 rounded">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-bold text-blue-800">站长回复</span>
                            <span className="text-xs text-blue-600">{msg.reply_at?.slice(0, 16).replace('T', ' ')}</span>
                          </div>
                          <p className="text-sm text-blue-900 whitespace-pre-wrap">{msg.reply}</p>
                        </div>
                      )}
                      
                      {/* 回复输入框 */}
                      {replyingMessageId === msg.id ? (
                        <div className="mb-2">
                          <div className="relative emoji-picker-container">
                            <textarea
                              value={replyContent}
                              onChange={(e) => setReplyContent(e.target.value)}
                              placeholder="请输入回复内容..."
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-12"
                              rows={3}
                            />
                            <button
                              type="button"
                              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                              className="absolute right-3 bottom-3 text-2xl hover:scale-110 transition"
                              title="添加表情"
                            >
                              😊
                            </button>
                            
                            {/* 表情选择器 */}
                            {showEmojiPicker && (
                              <div className="absolute right-0 top-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-20">
                                <div className="grid grid-cols-8 gap-1">
                                  {emojis.map((emoji, i) => (
                                    <button
                                      key={i}
                                      type="button"
                                      onClick={() => insertEmoji(emoji)}
                                      className="text-xl p-1 hover:bg-gray-100 rounded transition"
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => submitReply(msg.id)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              ✓ 提交
                            </button>
                            <button
                              onClick={() => {
                                cancelReply();
                                setShowEmojiPicker(false);
                              }}
                              className="text-gray-600 hover:text-gray-800 text-sm"
                            >
                              取消
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center text-xs text-gray-500 mb-2">
                          <span>IP: {msg.ip}</span>
                          <span>ID: {msg.id}</span>
                        </div>
                      )}
                      
                      <div className="flex gap-2 flex-wrap">
                        {/* 回复/编辑回复按钮 */}
                        {replyingMessageId !== msg.id && (
                          <button
                            onClick={() => startReply(msg.id, msg.reply || '')}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            💬 {msg.reply ? '编辑回复' : '回复'}
                          </button>
                        )}
                        
                        {/* 删除回复按钮 */}
                        {msg.reply && replyingMessageId !== msg.id && (
                          <button
                            onClick={() => deleteReply(msg.id)}
                            className="text-orange-600 hover:text-orange-800 text-sm"
                          >
                            🗑️ 删除回复
                          </button>
                        )}
                        
                        {/* 状态管理按钮 */}
                        {replyingMessageId !== msg.id && msg.status !== 1 && (
                          <button
                            onClick={() => updateMessageStatus(msg.id, 1)}
                            className="text-green-600 hover:text-green-800 text-sm"
                          >
                            ✓ 显示
                          </button>
                        )}
                        {replyingMessageId !== msg.id && msg.status !== 0 && (
                          <button
                            onClick={() => updateMessageStatus(msg.id, 0)}
                            className="text-yellow-600 hover:text-yellow-800 text-sm"
                          >
                            ⏳ 待审核
                          </button>
                        )}
                        {replyingMessageId !== msg.id && msg.status !== 2 && (
                          <button
                            onClick={() => updateMessageStatus(msg.id, 2)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            🚫 隐藏
                          </button>
                        )}
                        
                        {/* 删除留言按钮 */}
                        {replyingMessageId !== msg.id && (
                          <button
                            onClick={() => deleteMessage(msg.id)}
                            className="text-gray-600 hover:text-gray-800 text-sm ml-auto"
                          >
                            🗑️ 删除
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                
                {guestbookData.messages?.filter(
                  msg => guestbookFilter === 'all' || msg.status === guestbookFilter
                ).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    该状态下暂无留言
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 密码找回内容 */}
        {activeMainTab === 'password' && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-bold mb-4">🔑 密码找回</h2>
            <p className="text-sm text-gray-500 mb-4">
              输入用户名，解密还原其明文密码。仅限管理员使用。
            </p>
            
            <div className="flex gap-3 mb-4">
              <input
                type="text"
                value={recoverUsername}
                onChange={(e) => { setRecoverUsername(e.target.value); setRecoverResult(null); }}
                onKeyPress={(e) => e.key === 'Enter' && handleRecoverPassword()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="请输入用户名"
              />
              <button
                onClick={handleRecoverPassword}
                disabled={recoverLoading}
                className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 whitespace-nowrap"
              >
                {recoverLoading ? '查找中...' : '🔍 找回密码'}
              </button>
            </div>

            {/* 找回结果 */}
            {recoverResult && (
              <div className={`border rounded-lg p-4 ${
                recoverResult.error ? 'border-red-300 bg-red-50' :
                recoverResult.recovered ? 'border-green-300 bg-green-50' :
                'border-yellow-300 bg-yellow-50'
              }`}>
                {recoverResult.error ? (
                  <div className="flex items-center gap-2 text-red-700">
                    <span className="text-lg">❌</span>
                    <span>{recoverResult.error}</span>
                  </div>
                ) : recoverResult.recovered ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-700">
                      <span className="text-lg">✅</span>
                      <span className="font-medium">密码找回成功</span>
                    </div>
                    <div className="bg-white border border-green-200 rounded p-3 flex items-center justify-between">
                      <div>
                        <span className="text-gray-500 text-sm">用户名：</span>
                        <span className="font-bold">{recoverResult.username}</span>
                        <span className="mx-3 text-gray-300">|</span>
                        <span className="text-gray-500 text-sm">密码：</span>
                        <span className="font-bold text-blue-600 font-mono text-lg">{recoverResult.password}</span>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(recoverResult.password);
                        }}
                        className="text-gray-400 hover:text-blue-500 text-sm ml-2"
                        title="复制密码"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-yellow-700">
                    <span className="text-lg">⚠️</span>
                    <span>{recoverResult.message}</span>
                  </div>
                )}
              </div>
            )}

            {/* 使用说明 */}
            <div className="mt-6 text-xs text-gray-400 space-y-1">
              <p>· 密码采用 AES-256-CBC 对称加密，可通过用户名+服务端密钥解密还原</p>
              <p>· 旧格式(bcrypt)密码无法找回，需让用户登录一次后自动升级</p>
            </div>
          </div>
        )}
      </div>

      {/* IP详情模态框 */}
      {showIpModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50"
          onClick={() => setShowIpModal(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full mt-8 max-h-[calc(100vh-4rem)] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">
                🌍 IP列表与地域分布
                {selectedVersion !== 'all' && (
                  <span className="ml-2 text-base font-normal text-gray-600">
                    ({getVersionInfo(selectedVersion).name})
                  </span>
                )}
              </h2>
              <button 
                onClick={() => setShowIpModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>
            </div>
            
            <div className="p-4">
              {/* 标签页 */}
              <div className="flex border-b border-gray-200 mb-4">
                <button
                  className={`px-4 py-2 border-b-2 ${
                    activeTab === 'ip-list' 
                      ? 'border-blue-500 text-blue-600 font-medium' 
                      : 'border-transparent text-gray-500'
                  }`}
                  onClick={() => setActiveTab('ip-list')}
                >
                  IP列表
                </button>
                <button
                  className={`px-4 py-2 border-b-2 ${
                    activeTab === 'region-stats' 
                      ? 'border-blue-500 text-blue-600 font-medium' 
                      : 'border-transparent text-gray-500'
                  }`}
                  onClick={() => setActiveTab('region-stats')}
                >
                  地域分布
                </button>
              </div>

              {ipLoading ? (
                <div className="text-center py-8">加载中...</div>
              ) : !ipDetails ? (
                <div className="text-center py-8 text-gray-500">暂无数据</div>
              ) : (
                <>
                  {/* IP列表标签页 */}
                  {activeTab === 'ip-list' && (
                    <div className="space-y-1">
                      {ipDetails.ip_list?.map((item, index) => {
                        let location = item.country;
                        if (item.region) location += ' · ' + item.region;
                        if (item.city) location += ' · ' + item.city;
                        
                        return (
                          <div key={index} className="flex flex-wrap items-center gap-2 py-2 border-b border-gray-100 hover:bg-gray-50">
                            <div className="w-36 font-mono font-medium">{item.ip}</div>
                            <div className="flex-1 text-gray-600">{location}</div>
                            <div className="w-16 text-right font-medium">{item.count}</div>
                            <div className="w-40 text-right text-gray-400 text-sm">
                              {item.last_seen?.slice(0, 16).replace('T', ' ')}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* 地域分布标签页 */}
                  {activeTab === 'region-stats' && (
                    <div className="space-y-1">
                      {(() => {
                        const maxCount = Math.max(...(ipDetails.region_stats?.map(r => r.count) || [0]));
                        return ipDetails.region_stats?.map((item, index) => (
                          <div key={index} className="flex flex-wrap items-center gap-4 py-3 border-b border-gray-100">
                            <div className="flex-1">{item.region}</div>
                            <div className="w-48 h-3 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-green-500 rounded-full"
                                style={{ width: `${(item.count / (maxCount || 1)) * 100}%` }}
                              />
                            </div>
                            <div className="w-20 text-right font-medium">{item.count}</div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Stats;
